import type { Adjustment, MaterialInfo, PartDefinition } from '@rogue/rackbuilder'
import type { RackOptions } from '../../../RackTypes'
import { getRackModel } from '../../../modelPaths'
import { MaterialBuilder } from './materialBuilder'

/**
 * Handles front spanning crossmember (pullup bar area)
 * - Pullup bars (skinny, fat, fat/skinny combo)
 * - Socket pullup bars (with curl bar option)
 * - Crown pullup bar
 */
export class FrontCrossmemberBuilder {
  private frontLine: string
  private isAddon: boolean
  
  constructor(
    private config: RackOptions,
    private material: MaterialInfo,
    private materialBuilder: MaterialBuilder
  ) {
    this.frontLine = `${config.line}_PULLUP`
    this.isAddon = !!this.config.isAddon
  }
  
  /**
   * Creates the front spanning connection
   * Attaches to left upright, spans to right upright
   */
  // widthOverride and materialsOverride are both Hybrid-only; every other caller leaves them undefined and gets the 43" part in the rack's own color.
  createFrontCrossmember(widthOverride?: string, materialsOverride?: MaterialInfo): PartDefinition | undefined {
    if (!this.config.front) return undefined

    if (this.config.front.toLowerCase().startsWith('socket')) {
      return this.createSocketPullupBar()
    }

    if (this.config.front === 'crown') {
      return this.createCrown()
    }

    if (this.config.front === 'crossmember top 4') {
      return this.createCrossmember(widthOverride, materialsOverride)
    }

    return this.createStandardPullupBar()
  }

  private createAddonMaterials() {
    return this.materialBuilder.createAddonMaterials()
  }

  private createCrossmember(widthOverride?: string, materialsOverride?: MaterialInfo): PartDefinition {
    // 70" units borrow the rig's 70" depth beam ("crossmember top 7", each line's own _HYBRID_LARGE chunk); it is anchored and turned differently from the 43" part, see below.
    const is70Width = widthOverride === '70"'

    // The 70" part is only manufactured in black, but that is enforced by filtering the option list (hybrid-rules) rather than here, so the finish always follows what the customer actually selected.
    const materials = this.isAddon
      ? this.materialBuilder.createFrontCrossmemberAddonMaterials()
      : this.materialBuilder.createFrontCrossmemberMaterials(materialsOverride)

    const adjustments: Adjustment[] = [{ type: 'rotate', axis: 'Y', amount: 90 }]

    // The 70" crossmember is modeled facing the opposite way from the standard crossmember meshes. Cave corrects its copy with a Z flip, but a flip is a mirror - it reverses the branding - and with the span and the up axis both fixed no pure rotation can turn only the facing. Anchoring the far end instead lets a real 180 do it: 'front' spans back toward the unit, so the extra turn lands it the same way round without mirroring.
    if (is70Width) adjustments.push({ type: 'rotate', axis: 'Y', amount: 180 })

    return {
      // Only the Hybrid reaches this branch - PowerRackBuilder's crossmemberWidth is undefined and the rig is the one builder that overrides it - so the key always resolves to the rig's own chunk.
      mesh: is70Width
        ? getRackModel(`${this.config.line}_HYBRID_LARGE`, 'crossmember top 7')
        : getRackModel(`${this.config.line}_CROSSMEMBERS`, 'crossmember top 4'),
      slots  : {},
      // The ML beam's 'front_top' anchor is authored for the 'top' slot, which sits on the side ladder 1" above the 'crossmember-top' hole 'front' is made for.
      anchor : is70Width ? (this.config.line === 'RML' ? 'front_top' : 'front') : 'snowman_bottom',
      materials,
      adjustments,
      // 'front' sits 0.93" higher up the part than 'snowman_bottom' (mesh y -0.48 vs -1.41), which would otherwise drop the crossmember by that much against the 43" ones.
      offset : is70Width && this.config.line !== 'RML' ? { y: 0.93 } : undefined,
    }
  }
  
  private createSocketPullupBar(): PartDefinition {
    const [socket, type = 'bar', color = 'matte', knurl] = this.config.front!.split(' ')
    const pullupMaterials = this.materialBuilder.createSocketPullupMaterials(color, knurl)
    
    return {
      mesh  : getRackModel(this.frontLine, 'socket'),
      slots : {
        bar: {
          mesh      : getRackModel(this.frontLine, `socket ${type}`),
          slots     : {},
          materials : pullupMaterials,
        },
      },
      materials: this.isAddon ? this.createAddonMaterials() : this.material,
    }
  }
  
  private createCrown(): PartDefinition {
    const crownMaterials = this.materialBuilder.createCrownMaterials()
    
    return {
      mesh      : getRackModel(this.frontLine, 'crown'),
      anchor    : 'left',
      materials : this.isAddon ? this.createAddonMaterials() : crownMaterials,
      slots     : {},
      offset    : this.config.line === 'RML' ? { y: -4 } : {},
    }
  }
  
  private createStandardPullupBar(): PartDefinition {
    const isRM43PullupBars = (this.config.front?.includes('fat') || this.config.front?.includes('skinny')) 
      && (this.config.line === 'RM')
    
    const isIndy = this.config.leftSide || this.config.rightSide
    const requiredOffsetPUBar = (this.config.height === '80' && !this.config.halfRack) 
      || (isIndy && this.config.height === '80')
    
    let pullupBarOffset: { x?: number; y?: number } | undefined
    
    if (this.config.line === 'RML' && this.config.height === '80') {
      pullupBarOffset = { y: -0.4 }
    } else if (this.config.line === 'RM' && this.config.height === '80') {
      pullupBarOffset = { x: 0.05 }
    } else if (isRM43PullupBars) {
      pullupBarOffset = { x: 0.12 }
    }
    
    const frontMaterials = this.materialBuilder.createFrontCrossmemberMaterials()

    return {
      mesh: requiredOffsetPUBar 
        ? getRackModel(this.frontLine, 'offset') 
        : getRackModel(this.frontLine, this.config.front!),
      slots       : {},
      anchor      : this.config.front!.includes('crossmember') ? 'snowman_bottom' : 'left',
      materials   : frontMaterials,
      adjustments : this.config.front!.includes('crossmember')
        ? [{ type: 'rotate', axis: 'Y', amount: 90 }]
        : [],
      offset  : pullupBarOffset,
      effects : this.isAddon ? { opacity: 0.2 } : {},
    }
  }
}